/**
 * OnboardingContext - Manages wizard + tutorial state.
 *
 * Flow: user logs in → if !onboarding_completed → show wizard → show tutorial → mark complete.
 */

import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { authApi } from '../services/authApi';
import { captureAppEvent } from '../lib/posthogTracking';

const PUBLIC_ROUTES = ['/', '/login', '/register', '/verify', '/forgot-password', '/reset-password', '/auth/sso-callback'];

const WIZARD_STEP_NAMES = ['welcome', 'theme', 'language', 'currency', 'ready'] as const;

interface OnboardingContextType {
    showWizard: boolean;
    showTutorial: boolean;
    wizardStep: number;
    tutorialStep: number;
    totalTutorialSteps: number;
    setWizardStep: (step: number) => void;
    nextWizard: () => void;
    backWizard: () => void;
    finishWizard: (startTour: boolean) => void;
    nextTutorial: () => void;
    backTutorial: () => void;
    skipTutorial: () => void;
    completeTutorial: () => void;
    setTotalTutorialSteps: (n: number) => void;
}

const OnboardingContext = createContext<OnboardingContextType | undefined>(undefined);

export const useOnboarding = (): OnboardingContextType => {
    const ctx = useContext(OnboardingContext);
    if (!ctx) throw new Error('useOnboarding must be used within OnboardingProvider');
    return ctx;
};

const WIZARD_STEPS = 5; // Welcome, Theme, Language, Currency, Ready

/** Dedupe identical step views within a short window (React Strict Mode remounts in dev). */
let lastOnboardingViewKey = '';
let lastOnboardingViewAt = 0;
function captureOnboardingView(key: string, fire: () => void) {
    const now = Date.now();
    if (lastOnboardingViewKey === key && now - lastOnboardingViewAt < 250) return;
    lastOnboardingViewKey = key;
    lastOnboardingViewAt = now;
    fire();
}

export const OnboardingProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const { user } = useAuth();
    const location = useLocation();
    const isPublicRoute = PUBLIC_ROUTES.some(r => location.pathname === r);
    const needsOnboarding = !!user && !user.preferences?.onboarding_completed && !isPublicRoute;

    const [showWizard, setShowWizard] = useState(needsOnboarding);
    const [showTutorial, setShowTutorial] = useState(false);
    const [wizardStep, setWizardStep] = useState(0);
    const [tutorialStep, setTutorialStep] = useState(0);
    const [totalTutorialSteps, setTotalTutorialSteps] = useState(9);

    // Re-evaluate when user or route changes
    React.useEffect(() => {
        if (user && !user.preferences?.onboarding_completed && !isPublicRoute) {
            setShowWizard(true);
        } else {
            setShowWizard(false);
            if (isPublicRoute) setShowTutorial(false);
        }
    }, [user, isPublicRoute]);

    // Wizard step views (deduped; ignore totalTutorialSteps churn for tutorial)
    React.useEffect(() => {
        if (!showWizard) return;
        const step = WIZARD_STEP_NAMES[wizardStep] || `step_${wizardStep}`;
        captureOnboardingView(`wizard:${wizardStep}`, () => {
            captureAppEvent('onboarding_step_viewed', { step, step_index: wizardStep });
        });
    }, [showWizard, wizardStep]);

    React.useEffect(() => {
        if (!showTutorial) return;
        captureOnboardingView(`tutorial:${tutorialStep}`, () => {
            captureAppEvent('tutorial_step_viewed', {
                step_index: tutorialStep,
                total_steps: totalTutorialSteps,
            });
        });
    }, [showTutorial, tutorialStep, totalTutorialSteps]);

    const nextWizard = useCallback(() => {
        setWizardStep((s) => Math.min(s + 1, WIZARD_STEPS - 1));
    }, []);

    const backWizard = useCallback(() => {
        setWizardStep((s) => Math.max(s - 1, 0));
    }, []);

    const markComplete = useCallback(async () => {
        if (user) {
            await authApi.updatePreferences({ onboarding_completed: true });
            // Update cached user so wizard doesn't reappear on refresh
            try {
                const raw = localStorage.getItem('auth_user');
                if (raw) {
                    const cached = JSON.parse(raw);
                    cached.preferences = { ...cached.preferences, onboarding_completed: true };
                    localStorage.setItem('auth_user', JSON.stringify(cached));
                }
            } catch { /* silent */ }
        }
    }, [user]);

    const finishWizard = useCallback((startTour: boolean) => {
        captureAppEvent('onboarding_completed', { started_tour: startTour });
        setShowWizard(false);
        if (startTour) {
            setTutorialStep(0);
            setShowTutorial(true);
        } else {
            markComplete();
        }
    }, [markComplete]);

    const nextTutorial = useCallback(() => {
        setTutorialStep((s) => {
            if (s + 1 >= totalTutorialSteps) {
                captureAppEvent('tutorial_completed', { last_step: s, total_steps: totalTutorialSteps });
                setShowTutorial(false);
                markComplete();
                return s;
            }
            return s + 1;
        });
    }, [totalTutorialSteps, markComplete]);

    const backTutorial = useCallback(() => {
        setTutorialStep((s) => Math.max(s - 1, 0));
    }, []);

    const skipTutorial = useCallback(() => {
        captureAppEvent('tutorial_skipped', { last_step: tutorialStep, total_steps: totalTutorialSteps });
        setShowTutorial(false);
        markComplete();
    }, [markComplete, tutorialStep, totalTutorialSteps]);

    const completeTutorial = useCallback(() => {
        captureAppEvent('tutorial_completed', { last_step: tutorialStep, total_steps: totalTutorialSteps });
        setShowTutorial(false);
        markComplete();
    }, [markComplete, tutorialStep, totalTutorialSteps]);

    return (
        <OnboardingContext.Provider value={{
            showWizard,
            showTutorial,
            wizardStep,
            tutorialStep,
            totalTutorialSteps,
            setWizardStep,
            nextWizard,
            backWizard,
            finishWizard,
            nextTutorial,
            backTutorial,
            skipTutorial,
            completeTutorial,
            setTotalTutorialSteps,
        }}>
            {children}
        </OnboardingContext.Provider>
    );
};
